import { useId, useMemo, useState } from 'react';

import { Button, Dialog, Field, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { CanhBaoTrongHop, LoiTrongHop } from '../../tenant/components/phan-form';
import { useChuyenBo, useSuaTep, useTatCaKho } from '../hooks/useTriThuc';
import { duoiCay, dungCay } from '../nghiep-vu/tri-thuc';
import type { Folder, KnowledgeFile } from '../types';

/** Giá trị của "Gốc bộ" trong ô chọn: `Select` của antd không nhận `null` làm giá trị một lựa chọn. */
const GOC = '__goc__';

/** Chuyển tệp sang thư mục khác trong cùng bộ (`PATCH /files/{id}` với `folder_id`). */
export function HopChonThuMuc({
  tep,
  thuMuc,
  open,
  onClose,
}: {
  tep: KnowledgeFile;
  thuMuc: readonly Folder[];
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const sua = useSuaTep();
  const [chon, setChon] = useState(tep.folder_id ?? GOC);
  const [loi, setLoi] = useState<unknown>(null);

  // Thụt đầu dòng theo độ sâu: người dùng thấy cây, không phải danh sách phẳng trùng tên.
  const tuyChon = useMemo(
    () => [
      { value: GOC, label: t('triThuc.thuMuc.gocBo') },
      ...duoiCay(dungCay(thuMuc)).map(({ thuMuc: f, sau }) => ({ value: f.id, label: `${'  '.repeat(sau + 1)}${f.name}` })),
    ],
    [thuMuc, t],
  );
  const coThayDoi = chon !== (tep.folder_id ?? GOC);

  const gui = async () => {
    if (!coThayDoi || sua.isPending) return;
    setLoi(null);
    try {
      await sua.mutateAsync({ tep, du: { folder_id: chon === GOC ? null : chon }, daXong: 'thuMuc' });
      onClose();
    } catch (err) {
      setLoi(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('triThuc.tep.chuyenThuMucTieuDe')}
      width={460}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {Boolean(loi) && <LoiTrongHop>{dichLoi(loi)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={sua.isPending} disabled={!coThayDoi}>
              {sua.isPending ? t('chung.dangLuu') : t('triThuc.tep.chuyenThuMuc')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <p className="break-all text-sm font-medium text-content-primary">{tep.file_name}</p>
        <Field label={t('triThuc.tep.fThuMucDich')}>
          {(p) => (
            <Select<string>
              id={p.id}
              className="w-full"
              showSearch={{ optionFilterProp: 'label' }}
              options={tuyChon}
              value={chon}
              onChange={(v) => {
                setChon(v);
                setLoi(null);
              }}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}

/**
 * Chuyển tệp sang bộ tri thức khác (`POST /files/{id}/move`). Tệp ra khỏi thư mục, về gốc bộ mới và được
 * nạp lại theo quy tắc chunk của bộ đó. Không có lựa chọn "bỏ khỏi bộ": màn này chưa có chỗ xem tệp không
 * thuộc bộ nào.
 */
export function HopChuyenBo({ tep, open, onClose }: { tep: KnowledgeFile; open: boolean; onClose: () => void }) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const chuyen = useChuyenBo();
  const kho = useTatCaKho(open);
  const [chon, setChon] = useState<string | undefined>();
  const [daGui, setDaGui] = useState(false);
  const [loi, setLoi] = useState<unknown>(null);

  const tuyChon = (kho.data ?? [])
    .filter((k) => k.id !== tep.knowledge_base_id)
    .map((k) => ({ value: k.id, label: `${k.name} (${k.code})` }));

  const gui = async () => {
    setDaGui(true);
    if (!chon || chuyen.isPending) return;
    setLoi(null);
    try {
      await chuyen.mutateAsync({ tep, kbId: chon });
      onClose();
    } catch (err) {
      setLoi(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('triThuc.tep.chuyenBoTieuDe')}
      width={480}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {Boolean(loi) && <LoiTrongHop>{dichLoi(loi)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={chuyen.isPending}>
              {chuyen.isPending ? t('chung.dangLuu') : t('triThuc.tep.chuyenBo')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <p className="break-all text-sm font-medium text-content-primary">{tep.file_name}</p>
        <Field
          label={t('triThuc.tep.fBoDich')}
          required
          error={
            kho.isError
              ? t('triThuc.loi.danhSachBo', { loi: dichLoi(kho.error) })
              : daGui && !chon
                ? t('triThuc.tep.loiChuaChonBo')
                : undefined
          }
        >
          {(p) => (
            <Select<string>
              id={p.id}
              aria-describedby={p['aria-describedby']}
              status={p['aria-invalid'] ? 'error' : undefined}
              className="w-full"
              showSearch={{ optionFilterProp: 'label' }}
              placeholder={t('triThuc.chonBo')}
              loading={kho.isLoading}
              notFoundContent={kho.isLoading ? t('triThuc.dangTai') : t('triThuc.tep.khongCoBoKhac')}
              options={tuyChon}
              value={chon}
              onChange={(v) => {
                setChon(v);
                setLoi(null);
              }}
            />
          )}
        </Field>
        <CanhBaoTrongHop>{t('triThuc.tep.chuyenBoHauQua')}</CanhBaoTrongHop>
      </form>
    </Dialog>
  );
}
